
import Vue from 'vue';
import Spinner from '@/components/Spinner.vue';
import { NormalizedFormat, NormalizedIndexBase } from '@/types/apptypes';

type IndexWithExtraInfo = NormalizedIndexBase&{
	canSearch: boolean,
	canIndex: boolean,
	format: NormalizedFormat|undefined,
	searchUrl: string,
	sizeString: string,
	statusText: string,
	timeModified: string,
	timeModifiedFull: string
};

export default Vue.extend({
	components: {Spinner},
	props: {
		corpora: Array as () => NormalizedIndexBase[],
		formats: Array as () => NormalizedFormat[],
		title: String,
		isPrivate: Boolean,
		canCreateCorpus: Boolean,
		loading: Boolean
	},
	data: () => ({
		details: {} as Record<string, boolean>
	}),
	computed: {
		withExtraInfo(): IndexWithExtraInfo[] {
			// generate some data we need for rendering
			return this.corpora.map<IndexWithExtraInfo>(corpus => {
				let statusText: string = corpus.status;
				if (statusText === 'indexing') {
					statusText = ' (indexing) - ' + corpus.indexProgress!.filesProcessed + ' files, ' +
						corpus.indexProgress!.docsDone + ' documents, and ' +
						corpus.indexProgress!.tokensProcessed + ' tokens indexed so far...';
				} else if (corpus.status !== 'available') {
					statusText = ' (' + statusText + ')';
				} else  {
					statusText = '';
				}

				const format = this.formats.find(f => f.id === corpus.documentFormat)!;

				return {
					...corpus,
					canSearch: corpus.status === 'available',
					canIndex: corpus.status !== 'indexing' && corpus.status !== 'opening',
					format,
					searchUrl: CONTEXT_URL + '/' + corpus.id + '/search/',
					sizeString: this.abbrNumber(corpus.tokenCount),
					statusText,
					timeModified: this.dateOnly(corpus.timeModified),
					timeModifiedFull: corpus.timeModified
				};
			});
		}
	},
	methods: {
		corpusDisplayName(corpus: IndexWithExtraInfo) {
			// Check if corpus displayName is unique
			const otherCorpora = this.withExtraInfo.filter(c => c.id !== corpus.id);
			const isUnique = otherCorpora.every(c => c.displayName !== corpus.displayName);
			return isUnique ? corpus.displayName : corpus.displayName + ' (' + corpus.id + ')';
		},
		// 2695798 becomes 2,6M, etc.
		abbrNumber(n: number|null|undefined) {
			if (n == null) {
				return '';
			}
			let unit = '';
			if (n >= 1e9) {
				n = Math.round(n / 1e8) / 10;
				unit = 'G';
			} else if (n >= 1e6) {
				n = Math.round(n / 1e5) / 10;
				unit = 'M';
			} else if (n >= 1e3) {
				n = Math.round(n / 1e2) / 10;
				unit = 'K';
			}
			return String(n).replace(/\./, ',') + unit;
		},
		// Return only the date part of a date/time string,
		// and flip it around, e.g.:
		// "1970-02-01 00:00:00" becomes "01-02-1970"
		dateOnly(dateTimeString: string) {
			if (dateTimeString) {
				return dateTimeString.replace(/^(\d+)-(\d+)-(\d+) .*$/, '$3-$2-$1');
			} else {
				return '01-01-1970';
			}
		}
	}
})
